/**
 * Role-Based Access Control (RBAC) Configuration
 * Based on the permissions matrix from the Development Dashboard
 * 
 * Permission levels:
 * - view: Can view the page/feature
 * - edit: Can view and modify the page/feature
 * - none: No access
 */

// Role mappings for consistent naming
export const ROLES = {
  LEADERSHIP: ['admin', 'school_admin', 'district_admin'],
  TEACHER: ['teacher', 'special_services_teacher'],
  FAMILY: ['parent', 'family'],
  STUDENT: ['student'],
  SUPERADMIN: ['superadmin'],
  DEVELOPER: ['developer'],
};

// Flatten all roles for easy checking
export const ALL_ROLES = [
  ...ROLES.LEADERSHIP,
  ...ROLES.TEACHER,
  ...ROLES.FAMILY,
  ...ROLES.STUDENT,
  ...ROLES.SUPERADMIN,
  ...ROLES.DEVELOPER,
];

/**
 * Page/Feature permissions matrix
 * Key: route path or feature identifier
 * Value: object with role permissions (view, edit, none)
 */
export const PERMISSIONS = {
  // Home
  home: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'view',
    student: 'view',
  },

  // Analytics
  analytics: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'none',
    student: 'none',
  },

  // Rosters (Roster Upload/Project Creation)
  rosters: {
    leadership: 'edit',
    teacher: 'none', // submit request only
    family: 'view',
    student: 'none',
  },

  // Change Requests
  changeRequests: {
    leadership: 'edit',
    teacher: 'none', // submit request only
    family: 'none',
    student: 'none',
  },

  // Classes
  classes: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'view',
    student: 'view',
  },

  // Student Profiles
  studentProfiles: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'edit',
    student: 'view',
  },

  // Grading Section
  grading: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'view',
    student: 'view',
  },

  // Grade Entry
  gradeEntry: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'none',
    student: 'none',
  },

  // Enter Data Points (Standards Progression)
  dataEntry: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'view',
    student: 'none',
  },

  // Assignments & Assessments
  assignments: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'view',
    student: 'view',
  },

  // View Data
  viewData: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'view',
    student: 'view',
  },

  // Tiered Triangles
  tieredTriangles: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'view',
    student: 'none',
  },

  // Reports (basic and expanded)
  reports: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'none',
    student: 'none',
  },

  // Data Points Report
  dataPointsReport: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'none',
    student: 'none',
  },

  // Action Section
  action: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'view',
    student: 'view',
  },

  // Goals (active and completed)
  goals: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'view',
    student: 'edit',
  },

  // Action Cards
  actionCards: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'view',
    student: 'none',
  },

  // Teacher Profiles
  teacherProfiles: {
    leadership: 'edit',
    teacher: 'none',
    family: 'none',
    student: 'none',
  },

  // Parent account setup invitations
  userManagement: {
    leadership: 'edit',
    teacher: 'none',
    family: 'none',
    student: 'none',
  },

  // Requests
  requests: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'edit',
    student: 'edit',
  },

  // Connect
  connect: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'edit',
    student: 'edit',
  },

  // Messaging
  messaging: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'edit',
    student: 'edit',
  },

  // Pages
  pages: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'edit',
    student: 'edit',
  },

  // Calendars and Events
  calendars: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'edit',
    student: 'edit',
  },
  calendar: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'edit',
    student: 'edit',
  },

  // Communication Hub
  communication: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'view',
    student: 'view',
  },

  // Teacher Notes
  notes: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'none',
    student: 'none',
  },

  // Advance Management
  advanceManagement: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'edit',
    student: 'edit',
  },

  // Lost and Found
  lostAndFound: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'edit',
    student: 'edit',
  },

  // Professional Development
  professionalDevelopment: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'none',
    student: 'none',
  },

  // System Training
  systemTraining: {
    leadership: 'edit',
    teacher: 'none',
    family: 'view',
    student: 'none',
  },

  // Standard Proficiency
  standardProficiency: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'none',
    student: 'none',
  },

  // At Home Support
  atHomeSupport: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'edit',
    student: 'none',
  },

  // Help & Support
  helpSupport: {
    leadership: 'edit',
    teacher: 'edit',
    family: 'edit',
    student: 'edit',
  },
};

/**
 * Check if a user has permission to access a feature
 * @param {string} feature - Feature key from PERMISSIONS
 * @param {string|string[]} userRoles - User's role(s)
 * @param {string} requiredLevel - 'view' or 'edit'
 * @returns {boolean}
 */
export function hasPermission(feature, userRoles, requiredLevel = 'view') {
  const roles = Array.isArray(userRoles) ? userRoles : [userRoles];
  // Developer is a sole-purpose, most-restrictive role: it grants no
  // student-facing feature, even if mixed with another role (deny-wins,
  // mirroring the backend guard).
  if (ROLES.DEVELOPER.some((r) => roles.includes(r))) return false;
  const permissions = PERMISSIONS[feature];
  
  if (!permissions) return false;

  // Check each role
  for (const role of roles) {
    let roleCategory;
    
    if (ROLES.SUPERADMIN.includes(role)) {
      return true; // Superadmin has all permissions
    } else if (ROLES.LEADERSHIP.includes(role)) {
      roleCategory = 'leadership';
    } else if (ROLES.TEACHER.includes(role)) {
      roleCategory = 'teacher';
    } else if (ROLES.FAMILY.includes(role)) {
      roleCategory = 'family';
    } else if (ROLES.STUDENT.includes(role)) {
      roleCategory = 'student';
    }

    if (roleCategory) {
      const permission = permissions[roleCategory];
      
      if (requiredLevel === 'view' && (permission === 'view' || permission === 'edit')) {
        return true;
      }
      if (requiredLevel === 'edit' && permission === 'edit') {
        return true;
      }
    }
  }

  return false;
}

/**
 * Get role category from user roles
 * @param {string|string[]} userRoles 
 * @returns {string}
 */
export function getRoleCategory(userRoles) {
  const roles = Array.isArray(userRoles) ? userRoles : [userRoles];

  // Most-restrictive wins, regardless of role order in the array.
  if (ROLES.DEVELOPER.some((r) => roles.includes(r))) return 'developer';
  for (const role of roles) {
    if (ROLES.SUPERADMIN.includes(role)) return 'superadmin';
    if (ROLES.LEADERSHIP.includes(role)) return 'leadership';
    if (ROLES.TEACHER.includes(role)) return 'teacher';
    if (ROLES.FAMILY.includes(role)) return 'family';
    if (ROLES.STUDENT.includes(role)) return 'student';
  }
  
  return null;
}

/**
 * Filter sidebar links based on user permissions
 * @param {Array} links - Array of link objects
 * @param {string|string[]} userRoles - User's role(s)
 * @returns {Array} Filtered links
 */
export function filterLinksByPermissions(links, userRoles) {
  return links
    .map(link => {
      // Use explicit feature key if provided, otherwise map from route
      const feature = link.feature || getFeatureFromRoute(link.to);
      
      // Check if user has access to this link
      if (!feature || !hasPermission(feature, userRoles, 'view')) {
        return null;
      }

      // If link has children, filter them too
      if (link.children && link.children.length > 0) {
        const filteredChildren = link.children.filter(child => {
          const childFeature = child.feature || getFeatureFromRoute(child.to);
          return childFeature && hasPermission(childFeature, userRoles, 'view');
        });

        // If no children remain after filtering, exclude the parent
        if (filteredChildren.length === 0) {
          return null;
        }

        return {
          ...link,
          children: filteredChildren,
        };
      }

      return link;
    })
    .filter(link => link !== null);
}

/**
 * Map route paths to feature permission keys
 * @param {string} route 
 * @returns {string|null}
 */
function getFeatureFromRoute(route) {
  const routeMap = {
    '/admin/home': 'home',
    '/teacher/home': 'home',
    '/parent/home': 'home',
    '/student/home': 'home',
    '/admin/classes': 'classes',
    '/teacher/classes': 'classes',
    '/admin/analytics': 'analytics',
    '/admin/project-creation': 'rosters',
    '/admin/change-requests': 'changeRequests',
    '/admin/student-profiles': 'studentProfiles',
    '/teacher/student-profiles': 'studentProfiles',
    '/parent/student-profiles': 'studentProfiles',
    '/student/student-profiles': 'studentProfiles',
    '/admin/grading': 'grading',
    '/teacher/grading': 'grading',
    '/parent/grading': 'grading',
    '/student/grading': 'grading',
    '/admin/grade-entry': 'gradeEntry',
    '/teacher/grade-entry': 'gradeEntry',
    '/admin/standards-progression': 'dataEntry',
    '/teacher/standards-progression': 'dataEntry',
    '/admin/standard-progressions': 'viewData',
    '/admin/data-points-report': 'dataPointsReport',
    '/teacher/data-points-report': 'dataPointsReport',
    '/admin/reports': 'reports',
    '/teacher/reports': 'reports',
    '/admin/action': 'action',
    '/teacher/action': 'action',
    '/parent/action': 'action',
    '/student/action': 'action',
    '/admin/action-cards': 'actionCards',
    '/teacher/action-cards': 'actionCards',
    '/teacher/guided-actions': 'goals',
    '/admin/teacher-profiles': 'teacherProfiles',
    '/admin/requests': 'requests',
    '/admin/rights-requests': 'requests',
    '/teacher/requests': 'requests',
    '/parent/requests': 'requests',
    '/student/requests': 'requests',
    '/admin/connect': 'connect',
    '/teacher/connect': 'connect',
    '/admin/messaging': 'messaging',
    '/teacher/messaging': 'messaging',
    '/parent/connect': 'connect',
    '/student/connect': 'connect',
    '/admin/professional-development': 'professionalDevelopment',
    '/teacher/professional-development': 'professionalDevelopment',
    '/admin/training-resources': 'professionalDevelopment',
    '/teacher/training-resources': 'professionalDevelopment',
    '/admin/help-support': 'helpSupport',
    '/teacher/help-support': 'helpSupport',
    '/parent/help-support': 'helpSupport',
    '/student/help-support': 'helpSupport',
    '/teacher/standard-progressions': 'standardProficiency',
    '/teacher/at-home-support': 'atHomeSupport',
    '/parent/at-home-support': 'atHomeSupport',
    '/parent/action/goals': 'goals',
    '/parent/action/assignments': 'action',
    '/student/assignments': 'action',
    '/student/work-gallery': 'action',
    '/parent/data-points-report': 'viewData',
    '/admin/action/goals': 'goals',
    '/student/todo': 'goals',
    '/student/grades': 'viewData',
    '/student/subjects': 'viewData',
    '/parent/student-progress': 'viewData',
  };

  return routeMap[route] || null;
}
